import { useState } from "react";

import { UserTableHeaderTitles } from "../../Constants";
import { ReactComponent as CheckIcon } from "../../../images/check.svg";

import PrivilegeSelector from "../Selectors/PrivilegeSelector";
import ActionButtons from "../Buttons/ActionButtons";
import ExpireDateComponent from "../ExpireDateComponent/ExpireDateComponent";
import ActivationStatusButton from "../Buttons/ActivationStatusButton";
import UserLastActivityComponent from "../UserLastActivityComponent/UserLastActivityComponent";

export default function UsersTable({ localization, companyId, userInfo, language, onUpdateUser, onDeleteUser, getUserID }) {
  const [userDrafts, setUserDrafts] = useState({});
  const [hoverLockedUserIds, setHoverLockedUserIds] = useState([]);

  // локальная проверка изменений на стороне Интерфейса пользователя
  const changeUser = (user, patch) => {
    setUserDrafts((current) => {
      const nextDraft = { ...current[user.id], ...patch };
      const hasChanges = Object.entries(nextDraft).some(([key, value]) =>
        key === "wsEnabled" ? Boolean(value) !== Boolean(user[key]) : value !== user[key],
      );

      if (!hasChanges) {
        const { [user.id]: removedDraft, ...remainingDrafts } = current;
        return remainingDrafts;
      }

      return { ...current, [user.id]: nextDraft };
    });
  };

  // фактическая отмена изменений, откат к Исходному состоянию на стороне Интерфейса
  const cancelUserChanges = (userId) => {
    setUserDrafts((current) => {
      const { [userId]: removedDraft, ...remainingDrafts } = current;
      return remainingDrafts;
    });
  };

  // обновляется Интерфейс пользователя и вызываем функции синхронизации с БД
  const saveUserChanges = async (userId) => {
    const draft = userDrafts[userId];

    getUserID(userId);

    if (!draft) return;

    const user = userInfo.find((item) => item.id === userId);

    if (!user) return;

    const updatedUser = {
      ...user,
      ...draft,
    };

    const isSaved = await onUpdateUser(updatedUser);

    if (isSaved) {
      cancelUserChanges(userId);
    }
  };

  const toggleUser = (user, wsEnabled) => {
    changeUser(user, { wsEnabled: !wsEnabled });

    setHoverLockedUserIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));

    setTimeout(() => {
      setHoverLockedUserIds((prev) => prev.filter((id) => id !== user.id));
    }, 500);
  };

  const formatActivityTitle = (activeAt) => {
    if (!activeAt) return "No activity data";

    const date = new Date(activeAt);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${localization.lastActivity[language]} ${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="hero-section__users-table">
      <table className="users-table">
        <colgroup>
          {UserTableHeaderTitles.map((item, index) => (
            <col key={index} style={{ width: item.width }} />
          ))}
        </colgroup>
        <thead>
          <tr className="not-hovered">
            {UserTableHeaderTitles.map((item) => (
              <th key={item.title} scope="col" className="users-table__title">
                <h3>{item.title ? localization[item.title][language] : ""}</h3>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {userInfo.map((user) => {
            const draft = userDrafts[user.id];
            const displayedUser = draft ? { ...user, ...draft } : user;
            const wsEnabled = Boolean(displayedUser.wsEnabled);

            const activityTitle = formatActivityTitle(user.activeAt);

            return (
              <tr key={user.id}>
                <td className="users-table__cell">
                  <p>{user.companyName}</p>
                </td>
                <td className="users-table__cell">
                  <div className="users-table__user-cell flex-row" title={activityTitle}>
                    <UserLastActivityComponent activeAt={user.activeAt} />
                    <p>{user.name}</p>
                  </div>
                </td>
                <td className="users-table__cell">
                  <p>{user.email}</p>
                </td>
                <td className="users-table__cell">
                  <p>{user.appVersion}</p>
                </td>
                <td className="users-table__cell">
                  <PrivilegeSelector
                    value={displayedUser.privilege}
                    language={language}
                    onChange={(privilege) => changeUser(user, { privilege })}
                  />
                </td>
                <td className="users-table__cell">
                  <button
                    className={`check-table-button ${wsEnabled ? "check-table-button--check" : ""} ${hoverLockedUserIds.includes(user.id) ? "hover-locked" : ""}`}
                    type="button"
                    aria-label={`Toggle WS access for ${user.name}`}
                    aria-pressed={wsEnabled}
                    onClick={() => toggleUser(user, wsEnabled)}
                  >
                    <CheckIcon className="check-table-button__icon" />
                  </button>
                </td>
                <td className="users-table__cell">
                  <ActivationStatusButton
                    value={displayedUser.activationStatus}
                    localization={localization}
                    language={language}
                    updateUserDraft={(newStatus) => changeUser(user, { activationStatus: newStatus })}
                  />
                </td>
                <td className="users-table__cell">
                  <p>{user.status}</p>
                </td>
                <td className="users-table__cell">
                  <ExpireDateComponent expireDate={user.expireDate} language={language} />
                </td>

                <td className="users-table__cell">
                  <ActionButtons
                    unitType={localization.userUnit[language]}
                    unitId={`${companyId}-${user.id}`}
                    isDirty={Boolean(draft)}
                    onCancel={() => cancelUserChanges(user.id)}
                    onSave={() => saveUserChanges(user.id)}
                    onDelete={() => onDeleteUser(companyId, user.id)}
                    localization={localization}
                    language={language}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
